import { motion } from 'framer-motion';
import { Truck, ShieldCheck, MapPin, Settings, ArrowRight, CheckCircle2, Compass } from 'lucide-react';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/ui/SEO';

export default function Transport() {
  const stats = [
    { label: "Års erfaring", value: "20" },
    { label: "Moderne vognpark", value: "100%" },
    { label: "Sikkerhed i top", value: "Høj" },
    { label: "Leveringssikkerhed", value: "99.9%" }
  ];

  const products = [
    {
      name: "Træpiller",
      description: "Høj kvalitet træpiller til biobrændsel, leveret direkte til din dør eller krannet på plads.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/1a389518-7117-4a21-aaf7-f5a7078e9b5f.jpg"
    },
    {
      name: "Barkflis",
      description: "Flot og holdbar barkflis til bede og haveanlæg, der holder ukrudt nede og beskytter planterne.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/657cd683-9d44-479f-a8c5-564c9caf9deb.jpg"
    },
    {
      name: "Træflis",
      description: "Lys og dekorativ træflis til stier, bede eller legepladser. Perfekt til afdækning.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/1178d8c0-d9f4-40c1-98e5-b9e278d03bec.jpg"
    },
    {
      name: "Sand",
      description: "Stabilgrus, støbesand, strandsand eller flisesand til alle typer belægnings- og byggeprojekter.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/f2387110-e5fe-4700-9ec1-fccc647b468f.jpg"
    },
    {
      name: "Grus",
      description: "Forskellige typer grus til indkørsler, stier og underlag. Leveres i præcis den mængde, du skal bruge.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/93dfb7d9-09f7-4f2d-86d1-6aaba5778ca2.jpg"
    },
    {
      name: "Alle slags sten",
      description: "Pyntesten, herregårdssten, granitskærver eller marksten. Vi har alle slags sten til dit anlæg.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/c3a191a3-2482-44e5-8ea0-2751cb34c5c6.jpg"
    },
    {
      name: "Muldjord",
      description: "Harpet muldjord til græsplæner, højbede og haveanlæg. Klar til udlægning.",
      image: "https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/42c9bad5-4912-43b6-9234-f6c6a35e3a39.jpg"
    }
  ];

  return (
    <>
      <SEO 
        title="Transport med salg og levering" 
        description="Vi sætter en ære i at overholde aftalte betingelser og leveringstider. Vi leverer træpiller, barkflis, træflis, sand, grus, alle slags sten og muld. Vi kraner det ind i haven." 
      />

      {/* Hero */}
      <section className="pt-40 pb-20 bg-slate-50 text-slate-900 relative overflow-hidden">
        {/* Skewed Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1d398e] hidden lg:block skew-x-[-12deg] translate-x-20 z-0" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#1d398e] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1d398e] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1d398e]"></span>
              </span>
              <span className="tracking-wide uppercase">Salg & Levering</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] tracking-wide text-slate-900">
              Transport med <br />
              <span className="text-[#1d398e]">salg og levering</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              Vi sætter en ære i at overholde aftalte betingelser og leveringstider. Vi leverer alt i materialer direkte til dig – og vi kan krane det lige ind i haven.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Delivery Info Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-6">Vi overholder altid vores aftaler</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Hos NR. GALTEN VOGNMANDSFORRETNING ApS er en aftale en aftale. Vi ved, hvor vigtigt det er for dit projekt, at materialerne ankommer til tiden og i den aftalte kvalitet. Derfor sætter vi en stor ære i altid at overholde vores leveringstider og betingelser.
              </p>
              <p className="font-semibold text-slate-900">
                Har du brug for at få materialerne leveret et bestemt sted? Vi råder over biler med kran, så vi kan krane dine varer direkte ind i haven, over hækken eller ind i indkørslen, så du slipper for unødigt slæb.
              </p>
              
              <div className="grid grid-cols-2 gap-6 pt-4">
                {stats.map((stat, index) => (
                  <div key={index} className="border-l-4 border-blue-900 pl-4">
                    <p className="text-2xl font-bold text-blue-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/11d8ac04-b4a2-4792-97df-3ed8383a3251.jpg" 
              alt="Levering med kranbil" 
              className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </Section>

      {/* Products Grid */}
      <Section light>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-xl md:text-4xl font-bold text-blue-900 mb-4">Materialer vi leverer</h2>
          <p className="text-gray-600">
            Vi tilbyder salg og levering af en lang række kvalitetsprodukter til både have, anlæg og byggeri.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300 overflow-hidden flex flex-col">
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-48 object-cover"
              />
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Sand & Grus Delivery Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/52ad64ed-5451-45d9-a7a2-f3d8148f1648.jpg" 
              alt="Sand og grus levering" 
              className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-blue-900 text-white p-6 rounded-2xl shadow-xl hidden md:block max-w-xs">
              <p className="font-bold text-lg mb-1">Hurtig levering</p>
              <p className="text-sm text-blue-100">Vi leverer i hele lokalområdet med vores egne biler.</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-blue-900 font-bold uppercase tracking-wider text-sm block mb-2">Salg og Levering</span>
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6">Hurtig levering af sand & grus</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Vi tilbyder hurtig og effektiv levering af alle typer sand og grus til både private haveprojekter og professionelle byggepladser. Uanset om du skal bruge støbesand til fundamentet, strandsand til sandkassen, stabilgrus til indkørslen eller flisesand til belægningen, har vi det rette materiale.
              </p>
              <p>
                Vores erfarne chauffører leverer i hele lokalområdet med vores egne biler. Vi kan tippe materialerne af, hvor du ønsker det, eller vi kan krane det direkte ind over hækken, hegnet eller indkørslen, så du slipper for unødigt og hårdt slæbearbejde.
              </p>
              <ul className="space-y-3 pt-2">
                <li className="flex items-center space-x-3 text-slate-900 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-blue-900 shrink-0" />
                  <span>Alle former for sand</span>
                </li>
                <li className="flex items-center space-x-3 text-slate-900 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-blue-900 shrink-0" />
                  <span>Alle former for sten materialer</span>
                </li>
                <li className="flex items-center space-x-3 text-slate-900 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-blue-900 shrink-0" />
                  <span>Harpet mul, barkflis og traflis</span>
                </li>
                <li className="flex items-center space-x-3 text-slate-900 font-medium">
                  <CheckCircle2 className="w-5 h-5 text-blue-900 shrink-0" />
                  <span>Fleksibel levering med tipvogn og kranbil</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Crane Service Highlight */}
      <Section light>
        <div className="bg-gradient-to-br from-blue-900 to-blue-950 rounded-[3rem] p-8 md:p-16 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Truck className="w-64 h-64" />
          </div>
          <div className="relative z-10 max-w-3xl">
            <span className="bg-white/10 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block">
              Nem og skånsom levering
            </span>
            <h2 className="text-3xl md:text-5xl font-black mb-6">Vi kan kran det ind i haven!</h2>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">
              Undgå besværet med at flytte tunge materialer fra indkørslen. Med vores kranbiler kan vi løfte træpiller, flis, grus, sten og muld direkte ind over hækken eller hegnet og placere det præcis, hvor du skal bruge det. Det sparer dig for tid og hårdt arbejde.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button to="/kontakt" className="bg-amber-500 hover:bg-amber-600 text-white border-none">
                Bestil levering nu
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-xl md:text-3xl font-bold mb-6">Brug for materialer eller transport?</h2>
          <p className="text-blue-100 mb-10">
            Kontakt os i dag for priser på materialer og levering. Vi giver altid et uforpligtende tilbud.
          </p>
          <Button to="/kontakt" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
            Få et uforpligtende tilbud <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Section>
    </>
  );
}
